'use client';
import React, { useState } from 'react';

type CaseType = 'upper' | 'lower' | 'title' | 'sentence' | 'camel' | 'snake' | 'kebab' | 'alternating';

function convertCase(text: string, type: CaseType): string {
  switch (type) {
    case 'upper': return text.toUpperCase();
    case 'lower': return text.toLowerCase();
    case 'title': return text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
    case 'sentence': return text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase());
    case 'camel': return text.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (_, c) => c.toUpperCase());
    case 'snake': return text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    case 'kebab': return text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    case 'alternating': return text.split('').map((c, i) => i % 2 === 0 ? c.toLowerCase() : c.toUpperCase()).join('');
    default: return text;
  }
}

const CASES: { id: CaseType; label: string; example: string }[] = [
  { id: 'upper', label: 'UPPER CASE', example: 'HELLO WORLD' },
  { id: 'lower', label: 'lower case', example: 'hello world' },
  { id: 'title', label: 'Title Case', example: 'Hello World' },
  { id: 'sentence', label: 'Sentence case', example: 'Hello world' },
  { id: 'camel', label: 'camelCase', example: 'helloWorld' },
  { id: 'snake', label: 'snake_case', example: 'hello_world' },
  { id: 'kebab', label: 'kebab-case', example: 'hello-world' },
  { id: 'alternating', label: 'aLtErNaTiNg', example: 'hElLo wOrLd' },
];

export default function CaseConverterTool() {
  const [text, setText] = useState('');
  const [activeCase, setActiveCase] = useState<CaseType>('title');
  const [copied, setCopied] = useState(false);

  const converted = text ? convertCase(text, activeCase) : '';

  const handleCopy = () => {
    if (!converted) return;
    navigator.clipboard.writeText(converted).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="font-semibold text-text-primary text-sm mb-2 block">Input Text</label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type or paste your text here…"
          className="w-full h-32 border border-gray-200 rounded-2xl px-4 py-3 text-sm text-text-primary resize-none focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue"
        />
      </div>

      <div>
        <label className="font-semibold text-text-primary text-sm mb-3 block">Choose Case</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {CASES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCase(c.id)}
              className={`px-3 py-2.5 rounded-xl text-xs font-medium transition-colors text-left ${activeCase === c.id ? 'bg-brand-blue text-white' : 'bg-surface text-text-secondary hover:bg-gray-100'}`}
            >
              <div className="font-semibold">{c.label}</div>
              <div className={`text-xs mt-0.5 ${activeCase === c.id ? 'text-blue-100' : 'text-text-muted'}`}>{c.example}</div>
            </button>
          ))}
        </div>
      </div>

      {converted && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="font-semibold text-text-primary text-sm">Result</label>
            <button onClick={handleCopy} className="text-xs px-3 py-1.5 rounded-lg bg-blue-50 text-brand-blue hover:bg-blue-100 transition-colors font-medium">
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div className="bg-surface rounded-2xl px-4 py-3 text-sm text-text-primary min-h-[80px] whitespace-pre-wrap break-words">
            {converted}
          </div>
        </div>
      )}
    </div>
  );
}
